import { AYK_STATUSES, IH_STATUSES, OFFER_STATUS, OFFER_STATUS_KEYS, STEP_DEF, TAEK_STATUSES } from 'utils/textUtil';
import { isDisposalPaymentFeatureEnabled } from 'constant/featureFlags';

/** BE `DisposalContractContributionType` değerleri; etiketler task metnindeki adlandırmadır. */
export const PAYMENT_TYPES = [
  { value: 'author', label: 'Yazar' },
  { value: 'supervisor', label: 'Madde Sorumlusu' },
  { value: 'committee_head', label: 'Heyet Başkanı' },
  { value: 'style_editor', label: 'Telif Sonrası' },
];

export const PAYMENT_STATUS_OPTIONS = [
  { value: 'paid', label: 'Ödendi' },
  { value: 'unpaid', label: 'Ödenmedi' },
];

/**
 * Teklif listesi filtrelerinin tek tanımı.
 *
 * Uygulanmış filtrelerin tek kaynağı tarayıcı URL'sidir. Bu modül URL'yi alan değerlerine çevirir (okuma) ve alan
 * değerlerini tekrar URL'ye yazar (yazma). Parametre adları BE'nin filtre adlarıdır, değiştirilmez; bu sayede eski
 * filtre modalının ürettiği, kaydedilmiş ya da paylaşılmış linkler de açılır.
 *
 * Değer biçimleri (`kind`):
 * - `multi`: id listesi, `string[]`. Tekrarsız ve sıralı tutulur.
 * - `select`: tek id ya da seçenek değeri, `string`.
 * - `text`: serbest metin, `string`.
 * - `boolean`: Evet/Hayır, `true | false`. Boş değer "Tümü" demektir.
 * - `flag`: yalnız işaretliyken URL'de olan seçenek, `true`.
 * - `dateRange`: `{ after?: 'YYYY-MM-DD', before?: 'YYYY-MM-DD' }`.
 *
 * Seçenekler ya `options` ile statik (etiket `label` ya da intl `labelId`) ya da `source` ile sorgudan gelir:
 * `committees`, `authors`, `articleGroups`, `articleTypes`, `e1Articles`.
 */

/**
 * Paneldeki grup sırası. Her zaman açık gelen gruplar en üstte, art arda durur.
 * `singleColumn`: grubun alanları masaüstünde de alt alta, tek sütunda dizilir (varsayılan iki sütun).
 */
export const FILTER_GROUPS = [
  { key: 'people', label: 'Heyet ve Kişiler' },
  { key: 'payment', label: 'Ödeme Durumu' },
  { key: 'process', label: 'Süreç ve Durum', singleColumn: true },
  { key: 'article', label: 'Madde Özellikleri' },
  { key: 'edition', label: 'Edisyon ve Gerekçe' },
];

/** Tarih aralığı değerinin biçimi (`dateRange` alanının `after`/`before`'u). */
export const FILTER_VALUE_DATE_FORMAT = 'YYYY-MM-DD';

/** Panelde ve etiketlerde gösterilen tarih biçimi; tablodaki tarihlerle aynı. */
export const FILTER_DISPLAY_DATE_FORMAT = 'DD/MM/YYYY';

/** Alan değerini id listesine çevirir: `multi` zaten liste, tek değer tek elemanlı liste, boş değer boş liste. */
export const toIds = (value) => {
  if (Array.isArray(value)) {
    return value;
  }
  return value ? [value] : [];
};

/** Statik seçeneğin görünen adı: `labelId` varsa çevirisi, yoksa `label`. */
export const getOptionLabel = (option, formatMessage) => (option.labelId ? formatMessage({ id: option.labelId }) : option.label);

const YES_NO_OPTIONS = [
  { value: true, label: 'Evet' },
  { value: false, label: 'Hayır' },
];

const compareValues = (a, b) => a.localeCompare(b, 'tr', { numeric: true });

const lastOf = (list) => list[list.length - 1];

const DATE_PATTERN = /^(\d{4}-\d{2}-\d{2})/;

const readDate = (list) => lastOf(list.map((raw) => raw.match(DATE_PATTERN)?.[1]).filter(Boolean));

const multi = (param) => ({
  kind: 'multi',
  params: [param],
  read: (get) => get(param),
  write: (values) => values.map((value) => [param, value]),
});

const single = (kind, param, accepts = () => true) => ({
  kind,
  params: [param],
  read: (get) => lastOf(get(param).filter(accepts)),
  write: (value) => [[param, value]],
});

/** `trueValue`/`falseValue` BE'nin tanıdığı değerlerdir; okurken başka bir değer yok sayılır. */
const boolean = (param, trueValue, falseValue) => ({
  kind: 'boolean',
  params: [param],
  read: (get) => {
    const raw = lastOf(get(param).filter((value) => value === trueValue || value === falseValue));
    return raw === undefined ? undefined : raw === trueValue;
  },
  write: (value) => [[param, value ? trueValue : falseValue]],
});

const flag = (param, paramValue) => ({
  kind: 'flag',
  params: [param],
  read: (get) => (get(param).includes(paramValue) ? true : undefined),
  write: () => [[param, paramValue]],
});

/** Tarihler gün olarak tutulur; BE'ye başlangıç günün başı, bitiş günün sonu olarak gider. */
const dateRange = (afterParam, beforeParam) => ({
  kind: 'dateRange',
  params: [afterParam, beforeParam],
  read: (get) => {
    const after = readDate(get(afterParam));
    const before = readDate(get(beforeParam));
    if (!after && !before) {
      return undefined;
    }
    return { ...(after && { after }), ...(before && { before }) };
  },
  write: ({ after, before }) => [...(after ? [[afterParam, `${after} 00:00`]] : []), ...(before ? [[beforeParam, `${before} 23:59`]] : [])],
});

const toStatusOptions = (statuses) => statuses.map(({ id, status }) => ({ value: String(id), label: status }));

const canSeePaymentStatus = (user) => isDisposalPaymentFeatureEnabled && Boolean(user?.isAdmin || user?.isOrderUnit);

/**
 * Alanlar panel ve URL sırasıyla tanımlıdır; aynı filtre kümesi her zaman bu sırayla yazılır.
 *
 * `parent`: alan yalnız `parent.when(üst alanın değeri)` doğruyken geçerlidir, değilse yazılmaz. `parent.implied`
 * tanımlıysa, alt alan URL'de olup üst alan olmadığında üst alan bu değerle okunur (eski linkler üst alanı her zaman
 * yazmıyordu).
 * `hintId`: formdaki tam soru metni; panel ve etiketlerde kısa `label` görünür, soru ipucunda durur.
 * `isVisible`: alan yalnız doğru döndüğünde okunur ve gösterilir. Gizli alanın parametresi ilk uygulamada URL'den düşer.
 */
export const OFFER_FILTER_FIELDS = [
  { key: 'ownerCommittee', group: 'people', label: 'Sorumlu İlim Heyeti', source: 'committees', ...multi('ownerCommittee.id[]') },
  {
    key: 'committeeContributions',
    group: 'people',
    label: 'Görüş İstenen İlim Heyeti',
    source: 'committees',
    ...multi('committeeContributions.committee[]'),
  },
  { key: 'owner', group: 'people', label: 'Formu Dolduran', ...single('text', 'owner') },
  { key: 'author', group: 'people', label: 'Madde Yazarı', source: 'authors', ...single('select', 'authors.author.id') },

  {
    key: 'step',
    group: 'process',
    label: 'Madde Süreci',
    options: Object.entries(STEP_DEF).map(([value, name]) => ({ value, labelId: `article.${name}.title` })),
    ...single('select', 'targetOffer.step'),
  },
  {
    key: 'status',
    group: 'process',
    label: 'Son Durum',
    options: OFFER_STATUS_KEYS.filter((value) => value !== '19').map((value) => ({ value, label: OFFER_STATUS[value] })),
    ...single('select', 'targetOffer.status'),
  },
  { key: 'committeeStatus', group: 'process', label: 'İH Durumu', options: toStatusOptions(IH_STATUSES), ...single('select', 'targetOffer.committeeStatus') },
  { key: 'dahStatus', group: 'process', label: 'TAEK Durumu', options: toStatusOptions(TAEK_STATUSES), ...single('select', 'targetOffer.dahStatus') },
  { key: 'dahHistory', group: 'process', label: 'TAEK İade Edilmişler', ...flag('historyDahStatus', '3') },
  { key: 'aykStatus', group: 'process', label: 'AYK Durumu', options: toStatusOptions(AYK_STATUSES), ...single('select', 'targetOffer.aykStatus') },
  { key: 'aykHistory', group: 'process', label: 'AYK İade Edilmişler', ...flag('historyAykStatus', '3') },
  { key: 'createdAt', group: 'process', label: 'Tarih Aralığı', ...dateRange('createdAt[after]', 'createdAt[before]') },

  { key: 'articleGroup', group: 'article', label: 'Ansiklopedi Grubu', source: 'articleGroups', ...single('select', 'articleGroup') },
  { key: 'articleType', group: 'article', label: 'Madde Türü', source: 'articleTypes', ...single('select', 'articleType') },
  {
    key: 'foreignAuthor',
    group: 'article',
    label: 'Yabancı Yazar Süreci',
    options: YES_NO_OPTIONS,
    // BE bu filtrede yalnız 1/0 değerlerini tanır, true/false sessizce yok sayılır.
    ...boolean('authors.author.isForeign', '1', '0'),
  },

  ...PAYMENT_TYPES.map(({ value: type, label }) => ({
    key: `paymentStatus.${type}`,
    group: 'payment',
    label: `${label} Ödemesi`,
    options: PAYMENT_STATUS_OPTIONS,
    isVisible: canSeePaymentStatus,
    ...single('select', `paymentStatus[${type}]`, (value) => PAYMENT_STATUS_OPTIONS.some((option) => option.value === value)),
  })),

  {
    key: 'firstEdition',
    group: 'edition',
    label: '1. edisyonda yer alma',
    hintId: 'article.form.articleBeenEdisionOne',
    options: YES_NO_OPTIONS,
    ...boolean('exists[oldTitleType]', 'true', 'false'),
  },
  {
    key: 'oldTitleType',
    group: 'edition',
    label: 'Yer alma şekli',
    hintId: 'article.form.atifTam',
    parent: { key: 'firstEdition', when: (value) => value === true, implied: true },
    options: [
      { value: '0', labelId: 'article.form.Tam' },
      { value: '1', labelId: 'article.form.Atif' },
    ],
    ...single('select', 'oldTitleType'),
  },
  {
    key: 'oldTitle',
    group: 'edition',
    label: 'İlgili madde',
    hintId: 'article.form.SearchEdisionOneLabel',
    source: 'e1Articles',
    parent: { key: 'oldTitleType', when: (value) => value !== undefined },
    ...single('select', 'oldTitle[]'),
  },
  {
    key: 'oldArticleWorkType',
    group: 'edition',
    label: 'Yapılacak çalışma',
    hintId: 'article.form.oldArticleWorkType',
    parent: { key: 'oldTitleType', when: (value) => value !== undefined },
    // Her seçenek yalnız `oldTitleType` değeri eşleşen yer alma şeklinde sunulur.
    options: [
      { value: '0', labelId: 'article.formWorkToBeRewrite', oldTitleType: '0' },
      { value: '2', labelId: 'article.formWorkToBeReview', oldTitleType: '0' },
      { value: '1', labelId: 'article.formWorkToBeSupply', oldTitleType: '0' },
      { value: '3', labelId: 'article.formWorkToBeCorrection', oldTitleType: '0' },
      { value: '4', labelId: 'article.formWorkToBeChangeToEncyclopedia', oldTitleType: '1' },
      { value: '5', labelId: 'article.formWorkToBeChangeToGlossary', oldTitleType: '1' },
    ],
    ...multi('oldArticleWorkType[]'),
  },
  {
    key: 'mentionedInOldArticles',
    group: 'edition',
    label: 'Madde metinlerinde geçme',
    hintId: 'article.form.NearstExpressionOldArticle',
    options: YES_NO_OPTIONS,
    ...boolean('jsonNullFilter[oldArticles]', 'true', 'false'),
  },
  {
    key: 'oldArticles',
    group: 'edition',
    label: 'Geçtiği maddeler',
    hintId: 'article.form.WhichOldArticles',
    source: 'e1Articles',
    parent: { key: 'mentionedInOldArticles', when: (value) => value === true, implied: true },
    ...multi('oldArticles[]'),
  },
  { key: 'detectReason', group: 'edition', label: 'Teklif gerekçesi', hintId: 'article.form.offerReason', ...single('text', 'detectReason') },
];

/** Arayüzü olmayan eski parametreler: okunmaz, başka alanı etkilemez, ilk uygulamada URL'den düşer. */
const LEGACY_PARAMS = ['otherCommittees.id[]', 'committeeContributions.type[]', 'isOffer'];

const FILTER_PARAMS = new Set([...OFFER_FILTER_FIELDS.flatMap((field) => field.params), ...LEGACY_PARAMS]);

/** Kullanıcının görebildiği alanlar; `user` mevcut kullanıcıdır (`useGetCurrentUserQuery`). */
export const getVisibleFilterFields = (user) => OFFER_FILTER_FIELDS.filter((field) => !field.isVisible || field.isVisible(user));

const isEmptyValue = (value) =>
  value === undefined ||
  value === null ||
  value === '' ||
  (Array.isArray(value) && value.length === 0) ||
  (typeof value === 'object' && !Array.isArray(value) && !value.after && !value.before);

/** Bozuk yüzde kodlaması içeren eski kodlanmamış metin (ör. `%yüz`) olduğu gibi okunur. */
const safeDecode = (text) => {
  try {
    return decodeURIComponent(text);
  } catch {
    return text;
  }
};

/** `?a=1&b=2` → `[{ raw, key, value }]`. Anahtar ve değer çözülür; `raw` yeniden yazmada aynen korunur. */
const splitSearch = (search) =>
  search
    .replace(/^\?/, '')
    .split('&')
    .filter(Boolean)
    .map((raw) => {
      const separator = raw.indexOf('=');
      const key = separator === -1 ? raw : raw.slice(0, separator);
      const value = separator === -1 ? '' : raw.slice(separator + 1);
      return { raw, key: safeDecode(key), value: safeDecode(value) };
    });

const cleanValue = (field, value) => {
  if (field.kind === 'multi') {
    return [...new Set(value.map(String).filter(Boolean))].sort(compareValues);
  }
  if (field.kind === 'select' || field.kind === 'text') {
    return String(value);
  }
  if (field.kind === 'dateRange') {
    return { ...(value.after && { after: value.after }), ...(value.before && { before: value.before }) };
  }
  if (field.kind === 'boolean') {
    return typeof value === 'boolean' ? value : undefined;
  }
  // flag: işaretsiz seçenek URL'de hiç yer almaz.
  return value === true ? true : undefined;
};

/**
 * Değerleri tek biçime getirir: boş alanları atar, çoklu değerleri tekrarsız sıralar, üst alanı uygun olmayan
 * bağımlı alanları (zincirleme) düşürür. Dönen nesnede yalnız değeri olan alanlar bulunur; anahtar sayısı uygulanmış
 * alan (etiket) sayısıdır.
 */
export const normalizeFilterValues = (values, fields = OFFER_FILTER_FIELDS) =>
  fields.reduce((result, field) => {
    const value = values[field.key];
    if (isEmptyValue(value)) {
      return result;
    }
    if (field.parent && !field.parent.when(result[field.parent.key])) {
      return result;
    }
    const cleaned = cleanValue(field, value);
    return isEmptyValue(cleaned) ? result : { ...result, [field.key]: cleaned };
  }, {});

/** URL arama metninden uygulanmış filtre değerlerini okur. */
export const readFilterValues = (search, fields = OFFER_FILTER_FIELDS) => {
  const params = splitSearch(search).reduce((map, { key, value }) => map.set(key, [...(map.get(key) ?? []), value]), new Map());
  const get = (param) => (params.get(param) ?? []).filter((value) => value !== '');

  const values = Object.fromEntries(fields.map((field) => [field.key, field.read(get)]));

  [...fields].reverse().forEach((field) => {
    const { parent } = field;
    if (parent && 'implied' in parent && !isEmptyValue(values[field.key]) && values[parent.key] === undefined) {
      values[parent.key] = parent.implied;
    }
  });

  return normalizeFilterValues(values, fields);
};

/** Değerleri URL parçasına yazar; değerler URL-encode edilir, parametre adları BE'nin adlarıyla düz kalır. */
export const writeFilterValues = (values, fields = OFFER_FILTER_FIELDS) => {
  const normalized = normalizeFilterValues(values, fields);
  return fields
    .filter((field) => field.key in normalized)
    .flatMap((field) => field.write(normalized[field.key]))
    .map(([param, value]) => `${param}=${encodeURIComponent(value)}`)
    .join('&');
};

/**
 * Yeni filtre değerleriyle gidilecek adresi hesaplar. Filtre dışındaki parametreler (arama, sayfa boyutu, sıralama)
 * aynen kalır; eski parametreler düşer.
 *
 * - Filtre kümesi değiştiyse sayfa 1'e döner ve yeni geçmiş kaydı açılır.
 * - Küme aynı ama URL tek biçimde değilse (eski link) sayfaya dokunmadan URL `replace` ile düzeltilir.
 * - URL zaten aynıysa `null` döner; gezinme yapılmaz.
 */
export const planFilterNavigation = (search, nextValues, fields = OFFER_FILTER_FIELDS) => {
  const segments = splitSearch(search);
  const currentFilters = writeFilterValues(readFilterValues(search, fields), fields);
  const nextFilters = writeFilterValues(nextValues, fields);
  const changed = currentFilters !== nextFilters;

  const rest = segments.filter(({ key }) => !FILTER_PARAMS.has(key)).map(({ raw, key }) => (changed && key === 'page' ? 'page=1' : raw));
  const nextSearch = [...rest, nextFilters].filter(Boolean).join('&');

  if (nextSearch === segments.map(({ raw }) => raw).join('&')) {
    return null;
  }
  return { search: nextSearch ? `?${nextSearch}` : '', replace: !changed };
};

export const DEFAULT_PAGE_SIZE = 20;

/** Sıralama BE'nin kendi parametresiyle tutulur: `order[alan]=asc|desc`. */
const ORDER_PARAM = /^order\[(\w+)\]$/;
const ORDER_DIRECTIONS = ['asc', 'desc'];

const toPositiveInt = (text, fallback) => {
  const number = Number.parseInt(text, 10);
  return number > 0 ? number : fallback;
};

/** Liste parametreleri: her biri URL'de nasıl tanınır ve nasıl yazılır. Boş/varsayılan değer parametreyi kaldırır. */
const LIST_PARAMS = {
  title: { matches: (key) => key === 'title', write: (title) => (title ? [['title', title]] : []) },
  page: { matches: (key) => key === 'page', write: (page) => (page > 1 ? [['page', page]] : []) },
  pageSize: { matches: (key) => key === 'pageSize', write: (pageSize) => (pageSize ? [['pageSize', pageSize]] : []) },
  order: {
    matches: (key) => ORDER_PARAM.test(key),
    write: ({ type, order }) => (type && ORDER_DIRECTIONS.includes(order) ? [[`order[${type}]`, order]] : []),
  },
};

/** URL'den liste durumunu okur: arama metni, sayfa, sayfa boyutu ve sıralama (`{ type, order }`, yoksa ikisi de `null`). */
export const readListParams = (search) => {
  const segments = splitSearch(search);
  const get = (param) => segments.find(({ key }) => key === param)?.value;
  const orderSegment = segments.find(({ key, value }) => ORDER_PARAM.test(key) && ORDER_DIRECTIONS.includes(value));

  return {
    title: get('title') ?? '',
    page: toPositiveInt(get('page'), 1),
    pageSize: toPositiveInt(get('pageSize'), DEFAULT_PAGE_SIZE),
    order: orderSegment ? { type: orderSegment.key.match(ORDER_PARAM)[1], order: orderSegment.value } : { type: null, order: null },
  };
};

/**
 * Liste parametrelerini değiştirilmiş yeni arama metnini döner. `changes` yalnız değişen parametreleri taşır
 * (`title`, `page`, `pageSize`, `order`); her biri URL'deki yerinde güncellenir, yoksa sona eklenir. Filtreler ve diğer
 * parametreler ham halleriyle korunur. Değişiklik verilmezse URL'nin tek biçimi döner; karşılaştırma için kullanılır.
 */
export const updateListParams = (search, changes) => {
  const names = Object.keys(changes);
  const encode = ([param, value]) => `${param}=${encodeURIComponent(value)}`;
  const written = new Set();

  const kept = splitSearch(search).flatMap(({ raw, key }) => {
    const name = names.find((candidate) => LIST_PARAMS[candidate].matches(key));
    if (!name) {
      return [raw];
    }
    if (written.has(name)) {
      return [];
    }
    written.add(name);
    return LIST_PARAMS[name].write(changes[name]).map(encode);
  });
  const appended = names.filter((name) => !written.has(name)).flatMap((name) => LIST_PARAMS[name].write(changes[name]).map(encode));

  const nextSearch = [...kept, ...appended].join('&');
  return nextSearch ? `?${nextSearch}` : '';
};
